import { getAppKey } from '@kurtosys/app-loader/dist/utils.js';
import { common } from '@kurtosys/ksys-app-template';
import { IAppRegistry } from '@kurtosys/types/app/IAppRegistry.js';
import { IInputs } from '@kurtosys/types/app/IInputs.js';
import { IManifest } from '@kurtosys/types/appsManager/IManifest.js';

import { IConfigurationBase } from '../models/IConfigurationBase.js';
import { IStyles } from '../models/IStyles.js';

import { triggerEvent } from './events.js';

export function getElementsForApp(manifest: IManifest): HTMLElement[] {
	const ksysAppTemplateIdDataKey = 'data-ksys-app-template-id';
	const ksysAppTemplateVersionDataKey = 'data-ksys-app-template-version';
	const elements = document.querySelectorAll(`[${ksysAppTemplateIdDataKey}="${manifest.ksysAppTemplateId}"]`);
	const elementsWithCombinedVersion = document.querySelectorAll(
		`[${ksysAppTemplateIdDataKey}="${manifest.ksysAppTemplateId}-${manifest.version}"]`,
	);
	let elementsCollection: HTMLElement[] = [];
	if (elements) {
		elementsCollection = Array.from(elements) as HTMLElement[];
		const versions = Object.keys(
			elementsCollection.reduce(
				(acc: { [key: string]: boolean }, element) => {
					const version = element.getAttribute(ksysAppTemplateVersionDataKey);
					if (version) {
						acc[version] = true;
					}
					return acc;
				},
				{ [manifest.version]: true },
			),
		);
		if (versions && versions.length > 0) {
			const highestVersion = versions.sort().reverse()[0];
			elementsCollection = elementsCollection.filter((element) => {
				const version = element.getAttribute(ksysAppTemplateVersionDataKey);
				return (!version && manifest.version === highestVersion) || version === manifest.version;
			});
		}
	}
	if (elementsWithCombinedVersion) {
		elementsCollection.push(...(Array.from(elementsWithCombinedVersion) as HTMLElement[]));
	}
	return elementsCollection;
}

function getAppRegistry(): IAppRegistry {
	const key = '__ksys-app-registry__';
	if (!(window as any)[key]) {
		(window as any)[key] = {};
	}
	return (window as any)[key];
}

export function registerAppStart(
	manifest: IManifest,
	appParamsHelper: common.helpers.AppParamsHelper<IInputs, IConfigurationBase, IStyles>,
) {
	if (manifest.packageName !== 'ksys-app-manager') {
		const appKey = getAppKey(manifest, appParamsHelper);
		const appRegistry = getAppRegistry();
		appRegistry[appKey] = {
			appKey,
			status: 'started',
		};
	}
}

export function registerAppLoaded(
	manifest: IManifest,
	appParamsHelper: common.helpers.AppParamsHelper<IInputs, IConfigurationBase, IStyles>,
) {
	if (manifest.packageName !== 'ksys-app-manager') {
		const appKey = getAppKey(manifest, appParamsHelper);
		const appRegistry = getAppRegistry();
		const app = appRegistry[appKey];
		if (app) {
			app.status = 'loaded';
		}
		triggerEvent(`ksys-app-loaded`, appParamsHelper.element);
		setDocDoneIfReady();
	}
}

export function areAllAppsLoaded() {
	const appRegistry = getAppRegistry();
	let allLoaded = true;
	for (const key of Object.keys(appRegistry)) {
		const app = appRegistry[key];
		allLoaded = allLoaded && app.status === 'loaded';
		if (!allLoaded) {
			break;
		}
	}
	return allLoaded;
}

export async function setDocDoneIfReady() {
	const allLoaded = areAllAppsLoaded();
	if (allLoaded) {
		const hasFonts = !!(document as any)['fonts'];
		const fontsReady = hasFonts && (await (document as any)['fonts'].ready);
		if (!hasFonts || (fontsReady && fontsReady.status === 'loaded')) {
			setTimeout(() => {
				(window as any)['docdone'] = true;
				triggerEvent('ksys-apps-loaded');
			}, 1000);
		}
	}
}

export function pivotCollection<T>(
	collection: T[],
	getKeyFn: (value: T) => string,
	getValueFn: (value: T) => any = (value) => value,
): { [key: string]: T } {
	return (collection || []).reduce((acc: { [key: string]: any }, element) => {
		const key = getKeyFn(element);
		acc[key] = getValueFn(element);
		return acc;
	}, {});
}
